/**
 * Route Constants
 * Centralized route definitions for type-safe navigation
 */

import {
  buildInviteAcceptancePath,
  INVITE_ACCEPTANCE_ROUTE_PATTERN,
} from '@skillspec/shared-types';

/**
 * Public routes
 * Accessible without authentication
 */
export const PUBLIC_ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ANALYZE: '/analyze',
  CHANGE_PASSWORD: '/change-password',
  JOIN_INVITE: INVITE_ACCEPTANCE_ROUTE_PATTERN,
} as const;

/**
 * Protected routes
 * Require authentication
 */
export const PROTECTED_ROUTES = {
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',

  // User Management
  USERS: '/users',
  USER_DETAIL: '/users/:id',
  USER_CREATE: '/users/new',
  USER_EDIT: '/users/:id/edit',

  // Skills
  SKILLS: '/skills',
  SKILL_DETAIL: '/skills/:id',
  SKILL_MANAGE: '/skills/manage',

  // Agents
  AGENTS: '/agents',

  // Workspaces
  WORKSPACE_SETTINGS: '/workspace/settings',
  WORKSPACE_MEMBERS: '/workspace/members',
  WORKSPACE_INVITES: '/workspace/invites',

  // Skill Dashboard
  SKILL_DASHBOARD: '/skill-dashboard',
  SKILL_TARGETS: '/workspace/targets',

  // Directives
  DIRECTIVES: '/directives',
  DIRECTIVE_DETAIL: '/directives/:id',

  // Billing
  BILLING: '/workspace/billing',
  PRICING: '/workspace/pricing',

  // Webhooks
  WEBHOOKS: '/workspace/webhooks',
  WEBHOOK_DETAIL: '/workspace/webhooks/:id',

  // Security
  BEST_PRACTICES: '/workspace/security/best-practices',

  // Enterprise Admin
  ADMIN_ENTERPRISE: '/admin/enterprise',
  ADMIN_ENTERPRISE_DETAIL: '/admin/enterprise/:workspaceId',
  CONNECTOR_GRANTS: '/admin/connector-grants',
} as const;

/**
 * All routes combined
 */
export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...PROTECTED_ROUTES,
} as const;

/**
 * Helper to generate routes with parameters
 */
export const generateRoute = {
  userDetail: (id: number | string) => `/users/${id}`,
  userEdit: (id: number | string) => `/users/${id}/edit`,
  skillDetail: (id: number | string) => `/skills/${id}`,
  workspaceSettings: () => '/workspace/settings',
  enterpriseAdmin: () => '/admin/enterprise',
  enterpriseAdminDetail: (workspaceId: number | string) => `/admin/enterprise/${workspaceId}`,
  connectorGrants: () => '/admin/connector-grants',
  joinInvite: buildInviteAcceptancePath,
} as const;

/**
 * Route titles for breadcrumbs and page titles
 */
export const ROUTE_TITLES: Record<string, string> = {
  [ROUTES.ANALYZE]: 'Skill Quality Analyser',
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.PROFILE]: 'Profile',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.USERS]: 'User Management',
  [ROUTES.USER_DETAIL]: 'User Details',
  [ROUTES.USER_CREATE]: 'Create User',
  [ROUTES.USER_EDIT]: 'Edit User',
  [ROUTES.SKILLS]: 'My Skills',
  [ROUTES.SKILL_DETAIL]: 'Skill Details',
  [ROUTES.SKILL_MANAGE]: 'Manage Skills',
  [ROUTES.AGENTS]: 'Agents',
  [ROUTES.WORKSPACE_SETTINGS]: 'Workspace Settings',
  [ROUTES.WORKSPACE_MEMBERS]: 'Members',
  [ROUTES.WORKSPACE_INVITES]: 'Invites',
  [ROUTES.SKILL_TARGETS]: 'Skill Targets',
  [ROUTES.DIRECTIVES]: 'Directives',
  [ROUTES.DIRECTIVE_DETAIL]: 'Directive Detail',
  [ROUTES.BILLING]: 'Billing',
  [ROUTES.PRICING]: 'Pricing',
  [ROUTES.WEBHOOKS]: 'Webhooks',
  [ROUTES.WEBHOOK_DETAIL]: 'Webhook Detail',
  [ROUTES.BEST_PRACTICES]: 'Security Best Practices',
  [ROUTES.ADMIN_ENTERPRISE]: 'Enterprise Administration',
  [ROUTES.ADMIN_ENTERPRISE_DETAIL]: 'Enterprise Workspace',
  [ROUTES.CONNECTOR_GRANTS]: 'Connector Grants',
};
